import React, { useState, useEffect } from 'react';
import { useParams, Switch, Route, Redirect, useRouteMatch } from "react-router-dom";
import { Layout, Link, Jumbotron, Stat, Panel, GateKeeper } from '@components'
import { Account, Asset } from '@archetypes'
import styled from "styled-components"
import assetController from '@app/App.Controller'
import appController, { Status } from '@app/App.Controller'
import { ReactComponent as IconMint } from "@assets/Icon_Mint_New.svg"
import { ReactComponent as IconAddCollateral } from "@assets/Icon_Add_Collateral.svg"
import { ReactComponent as IconReduceCollateral } from "@assets/Icon_Reduce_Collateral.svg"
import IconRedeemPNG from "@assets/Icon_Redeem.png"
import { ReactComponent as IconRefresh } from '@assets/Icon_Refresh.svg';
import { ReactComponent as ArrowRight } from '@assets/Icon_Arrow_Right.svg';
import { ReactComponent as ArrowLeft } from '@assets/Icon_Arrow_Left.svg';


const Extension = styled.div`
	background-color: #132A3E;
	display: flex;
	padding: 0;
	max-height: 0;
	overflow: hidden;
	transition: all 0.5s;
	flex-direction: column;
	margin-top: -5px;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;

	font-size: 14px;
	font-weight: 400;
	
	.-row {
        display: flex;
        flex-direction: row;
        padding: 20px;
        padding-top: 5px;
        padding-bottom: 5px;
        >div { 
            flex: 30%;
            color: var(--color-label);
            :last-child {
                flex: 70%;
                color: var(--color-white);
                text-align: right;
            }
        }
    }

    ${props => props.visible && `
        max-height: 200px;
        padding: 20px;
    `}

`

const Mint = () => {

	const { perlinx } = assetController
	const [stats, setStats] = useState()

	useEffect(() => {
		const sub = perlinx.state.subscribe('stats', setStats, true)
		return () => sub.unsubscribe()
	}, []) // eslint-disable-line

	return (<Layout>
		<Layout.Main>
			<Jumbotron
				title="Synthetic Assets"
				navs={
					<>
						<a href="https://medium.com/perlin-network" target="_blank"><Link.Info content="Learn More" /></a>
						<Link.Dashboard />
					</>}
			>
				<Stat title='Total Collateral Staked'>
					${(Number(stats?.totalCollateral) || 0).toLocaleString()}
				</Stat>
			</Jumbotron>

			<Layout.Section title='Your Positions'>
				<Account.CDPs />
			</Layout.Section>
			<Layout.Section title='All Synthetic Assets'>
				<Asset.All />
			</Layout.Section>
		</Layout.Main>
	</Layout>)
}

const ReturnIcon = styled(
	({ className, setPanel }) => (<ArrowLeft onClick={() => setPanel()} className={className} />))`
	width: 32px;
	height: 32px;
	cursor: pointer;
	margin-right: 2rem;
	`

const PanelHeader = styled(
	({ className, title, subtitle, setPanel }) => {
		return (<div style={{ display: "flex", flexDirection: "row" }}>
			<ReturnIcon
				setPanel={setPanel}
			/>
			<div className={className}>
				<h3>
					{title}
				</h3>
				<p>{subtitle}</p>
			</div>
		</div>)
	})`
		display: flex;
		flex-direction: column;
		h3 , p {
			margin: 0px;
			font-weight: 600;
		}
		h3 {
			font-size: 18px;
			line-height: 32px;
			color: var(--color-white);
		}
		p {
			font-size: 14px;
			line-height: 24px;
			color: var(--color-label, lightblue);
		}
	`

const panels = [
	{
		id: 1,
		title: "Mint assets",
		subtitle: "Mint synthetic assets by staking PERL as collateral",
		icon: <IconMint />
	},
	{
		id: 2,
		title: "Increase collateral",
		subtitle: "Improve your position by increasing staked collateral",
		icon: <IconAddCollateral />
	},
	{
		id: 3,
		title: "Reduce collateral",
		subtitle: "Reduce your collateral by withdrawing excess collateral",
		icon: <IconReduceCollateral />
	},
	{
		id: 4,
		title: "Redeem assets",
		subtitle: "Exchange synthetic assets for its underlying collateral",
		icon: IconRedeemPNG
	}
]

const AssetPanels = ({ address, panelId, setPanelId, setExtensions }) => {
	const { synthetics } = appController
	// const [panelId, setPanelId] = useState()
	const [myPosition, setMyPosition] = useState()

	useEffect(() => {
		const sub = synthetics.state.subscribe(`emps.${address}`, (emp) => {
			const { myPosition } = emp
			setMyPosition({ ...myPosition })
		}, true)
		return () => {
			sub.unsubscribe()
		}
	}, [address]) // eslint-disable-line

	const resolvePanel = (id, thisAddress) => {
		switch (id) {
			case 1:
				return <Asset.Mint.Panel setExtensions={setExtensions} address={thisAddress} />
			case 2:
				return <Asset.Increase.Panel setExtensions={setExtensions} address={thisAddress} />
			case 3:
				return <Asset.Withdraw.Panel setExtensions={setExtensions} address={thisAddress} />
			case 4:
				return <Asset.RedeemPanel setExtensions={setExtensions} address={thisAddress} />
		}
	}


	const filteredPanels = panels.reduce((output, panel) => {
		// show only Redeem
		if (myPosition?.expired) {
			if (panel.id === 4) {
				output.push(panel)
			}
		} else {
			// shows only Mint
			if (myPosition?.rawCollateral === "0") {
				if (panel.id === 1) {
					output.push(panel)
				}
			} else {
				// Show all
				output.push(panel)
			}
		}
		return output
	}, [])

	return (
		<GateKeeper
			controllerAssertions={[
				{
					controller: 'account',
					key: 'status',
					assert: val => val === Status.READY,
					// onFail: <Panel fadeIn>Connect your account to view the position</Panel>,
				},
			]}>
			{!panelId &&
				(
					<Panel fadeIn>
						<h4>
							Manage your position
                		</h4>

						<GateKeeper

							assertions={[
								{
									assert: () => !!myPosition,
									onFail: () => <Panel fadeIn><IconRefresh className='rotate' />Fetching position information</Panel>,
								}
							]}
							dependencies={[myPosition]}
						>
							<div className="-menu-wrapper">
								{filteredPanels.map((item, index) => (
									<div key={index}>
										<div className="-menu-item clickable" onClick={() => setPanelId(item.id)}>
											<div>
												{item.id !== 4 && item.icon}
												{item.id === 4 && <img src={item.icon} />}
											</div>
											<div className="description">
												<h4>{item.title}</h4>
												<p>{item.subtitle}</p>
											</div>
											<div>
												<ArrowRight className="arrow" />
											</div>
										</div>
										<Panel.Divider noMargin light />
									</div>
								))}
							</div>
						</GateKeeper>
					</Panel>
				)}
			{panelId && (
				<Panel fadeIn>
					<PanelHeader
						title={panels[panelId - 1].title}
						subtitle={panels[panelId - 1].subtitle}
						setPanel={setPanelId}
					/>
					<Panel.Divider light />
					{resolvePanel(panelId, address)}
				</Panel>
			)}
		</GateKeeper>
	)
}

const AssetDashboard = styled(
	({ className }) => {
		let { address } = useParams();

		const [panelId, setPanelId] = useState()
		const [extensions, setExtensions] = useState([])

		useEffect(() => {
			setExtensions([])
		}, [panelId])

		return (
			<>
				<Layout>
					<Layout.Main>
						<Layout.Section>
							<Asset.StatSection
								address={address}
							/>
						</Layout.Section>
					</Layout.Main>
				</Layout>
				<Layout className={className}>
					<Layout.Aside>
						<Asset.Position.V3
							address={address}
						/>
					</Layout.Aside>
					<Layout.Main>
						<AssetPanels
							address={address}
							panelId={panelId}
							setPanelId={setPanelId}
							setExtensions={setExtensions}
						/>
						<Extension
							visible={extensions.length > 0}
						>
							{extensions.map((extension, index) => (
								<div className="-row" key={index}>
									<div>{extension?.key}</div>
									<div>
										{extension?.value}
									</div>
								</div>
							))}
						</Extension>
					</Layout.Main>
				</Layout>
			</>
		)
	})`
	margin-top: 3rem;	
	h4 {
        font-size: 16px;
        margin: 0px;
        color: var(--color-label, lightblue);
	}
	.-menu-wrapper {
		padding-top: 3rem;
		padding-bottom: 3rem;

		.clickable {
			cursor: pointer;
		}

	}
	.-menu-item {
		display: flex;
		flex-direction: row;
		margin: 0px;
		padding-top: 2rem;
		padding-bottom: 2rem;

		>.description {
			flex-direction: column;
			margin-top: auto;
			margin-bottom: auto;
			h4,p {
				margin: 0px;
			}
			p, h4 {
				line-height: 24px;
			}
			h4 {
				color: var(--color-white);
				font-size: 16px;
			}
			p {
				color: var(--color-label, lightblue);
				font-size: 14px;
			}
		}
		svg {
			width: 48px;
			height: 48px;
			margin: auto;
		}
		img {
			margin: auto;
			width: 64px;
			height: 32px;
		}
		
		.arrow {
			width: 32px;
			height: 32px;
			margin: auto;
			cursor: pointer; 
		}
		div {
			flex 5;
			display: flex;
			:first-child, :last-child {
				flex 1;
			}
		}
	}

	`


export default ({ className }) => {
	let { path } = useRouteMatch()
	return (
		<Switch>
			<Route exact path={path} component={Mint} />
			<Route exact path={`${path}/:address`} component={AssetDashboard} />
			<Redirect to={path} />
		</Switch>
	)
}